import * as THREE from "three";
import type { Skater } from "../../skater";
import { heldItem, walkingWith, type ItemId } from "../../sim/backpack";
import { reachTo } from "../ik";
import type { Arm } from "../skater-parts";
import { GUN_AT, GUN_FOREGRIP, GUN_GRIP } from "./pack";
import { easeHold } from "./ride-pose";

// What's in the rider's hands on foot (#153): the paintball gun (#136) or
// the power washer's wand (#151) up in both hands, and the board strapped
// to the back whenever anything else is out of the pack. Each has its own
// hold, eased; `SkaterModel` reads them off this rig rather than keeping
// them as fields of its own. The rides walked beside you (#145) pose
// themselves (`RideRig`) and only read `walking` and `hold` here.

/** How fast the hands come up to the gun or the wand: about a third of a second, shouldered (#143). */
const HOLD_RATE = 9;
/** How fast the board goes onto the back or comes back to the arm: about 0.2 s (#153). */
const PACK_RATE = 14;

export class HeldRig {
  /** 0..1 how far the gun or the wand is up in both hands. */
  hold = 0;
  /** 0..1 how far the board is on the back instead of under the arm. */
  packed = 0;
  /** The item walked beside you this frame (`walkingWith`). */
  walking: ItemId | null = null;
  private readonly gun: THREE.Group;
  private readonly washer: { wand: THREE.Group; tank: THREE.Group };
  /** The gun or the wand, whichever is in your hands (or was last). */
  private held: THREE.Group;
  /** The shot count last frame, and 1..0 the recoil from the latest, eased out. */
  private lastShots = 0;
  private recoil = 0;

  constructor(rig: { gun: THREE.Group; washer: { wand: THREE.Group; tank: THREE.Group } }) {
    this.gun = rig.gun;
    this.washer = rig.washer;
    this.held = this.gun;
  }

  /** Eases each hold toward what's out of the pack this frame. */
  ease(sk: Skater, dt: number): void {
    const item = heldItem(sk);
    const gunOut = item === "paintball" || item === "washer";
    if (gunOut) this.held = item === "washer" ? this.washer.wand : this.gun;
    this.hold = easeHold(this.hold, gunOut, HOLD_RATE, dt);
    // Anything but the board out, the board goes on the back: the gun, the
    // washer, or a ride walked beside you.
    this.packed = easeHold(this.packed, item !== null && item !== "board", PACK_RATE, dt);
    this.gun.visible = item === "paintball";
    this.washer.wand.visible = this.washer.tank.visible = item === "washer";
    this.walking = walkingWith(sk);
    if (sk.pack.shots !== this.lastShots) this.recoil = sk.pack.shots > this.lastShots ? 1 : 0;
    this.lastShots = sk.pack.shots;
    this.recoil *= Math.exp(-14 * dt);
  }

  /**
   * The gun up in both hands: back hand on the grip, front hand on the
   * foregrip, kicking back a little with each shot; the washer's wand is
   * held on the same spots and doesn't kick. `hold` blends the arms in from
   * wherever the arm pose left them. Says whether it moved the arms.
   */
  pose(root: THREE.Object3D, armBack: Arm, armFront: Arm): boolean {
    const w = this.hold;
    this.gun.position.copy(GUN_AT);
    this.gun.position.x += 0.06 * this.recoil;
    this.gun.rotation.z = -0.12 * this.recoil;
    if (w <= 0.001) return false;
    const held = this.held;
    root.updateMatrixWorld(true);
    const pairs = [
      [armBack, GUN_GRIP],
      [armFront, GUN_FOREGRIP],
    ] as const;
    for (const [arm, spot] of pairs) {
      const free = [arm.shoulder.quaternion.clone(), arm.elbow.quaternion.clone()] as const;
      reachTo(arm, held.localToWorld(TMP_V.copy(spot)));
      if (w < 1) {
        arm.shoulder.quaternion.copy(free[0].slerp(arm.shoulder.quaternion, w));
        arm.elbow.quaternion.copy(free[1].slerp(arm.elbow.quaternion, w));
      }
    }
    return true;
  }
}

const TMP_V = new THREE.Vector3();
